/**
 * useMentalityDocument — owns the attached-document state for the
 * overlay: upload → preview → delete + restore-on-open.
 *
 * The server persists the pasted/uploaded document so it survives
 * refreshes and device switches.
 */

import { useCallback, useEffect, useState } from 'react';
import {
    uploadMentalityDocument,
    getMentalityDocument,
    deleteMentalityDocument,
} from '../../../mentorshipService';

type UseMentalityDocumentArgs = {
    open: boolean;
    cancelRef: React.MutableRefObject<boolean>;
};

export function useMentalityDocument({ open, cancelRef }: UseMentalityDocumentArgs) {
    const [docName, setDocName] = useState('');
    const [docText, setDocText] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadTruncated, setUploadTruncated] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        void getMentalityDocument()
            .then((doc) => {
                if (cancelRef.current) return;
                if (doc.document_name || doc.document_text) {
                    setDocName(doc.document_name);
                    setDocText(doc.document_text);
                    setUploadTruncated(doc.truncated);
                }
            })
            .catch(() => { /* no saved doc is fine */ });
    }, [open, cancelRef]);

    const upload = useCallback(async (file: File) => {
        setUploadError(null);
        setUploading(true);
        setUploadTruncated(false);
        try {
            const resp = await uploadMentalityDocument(file);
            setDocName(resp.document_name);
            setDocText(resp.document_text);
            setUploadTruncated(resp.truncated);
        } catch (err: unknown) {
            console.error('[MentalityOverlay] upload failed', err);
            const detail = (err as { response?: { data?: { detail?: string } } })
                ?.response?.data?.detail;
            setUploadError(detail || 'Upload failed. Try a different file or paste the text.');
        } finally {
            setUploading(false);
        }
    }, []);

    const clear = useCallback(() => {
        setDocName('');
        setDocText('');
        setUploadTruncated(false);
        void deleteMentalityDocument().catch(() => {});
    }, []);

    const dismissError = useCallback(() => setUploadError(null), []);

    return {
        docName, docText, uploading, uploadTruncated, uploadError,
        upload, clear, dismissError,
    };
}
